import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';

import type { Url } from 'next/dist/shared/lib/router/router';
import { useRouter } from 'next/router';
import { shallow } from 'zustand/shallow';
import BackButton from '../../../backNavigation/components/BackButton';

import type { OptimisticDraft } from '@/src/types/draftable';

import useOpenInTabs from '@/src/hooks/useOpenInTabs';
import useUIStore from '@/src/store/ui';

import ToggleResourceMobileMultiselectButton from '@/src/components/SelectionButton/SelectionButton';
import { ZoomSliderWithinDropdownV2 } from '@/src/components/ZoomSlider/ZoomSliderWithinDropdown';
import { useFilterToggleButton } from '@fabric-app/business/hooks/useFilterToggleButton';
import { useQueryResourceSummary } from '@fabric-app/business/resources/queries/useQueryResourceSummary';

import { DropdownItemExport } from '@fabric-app/business/export/components/DropdownItemExport';
import { ResourceUiDropdownShowControls } from '@fabric-app/business/resources/components/DropdownShowControls';

import { pick } from '@fabric-app/utils/object/manipulation';
import type { ResourceDetail, ResourceDetailFolder } from '@fabric-app/types/resources';
import { useContextShareToken } from '@fabric-app/business/permission/contexts/AnonShareTokenContext';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useResourcePermissionsByCurrentUser } from '@fabric-app/business/resource-detail/components/context/getDisabledActionsReasons';
import { useSocketIOParentRoomEventListener } from '@fabric-app/business/socket.io/ParentRoom/useSocketIOParentRoomEventListener';
import { resourceSharingModal } from '@fabric-app/business/permission/components/ModalShare/resourceSharingStore';
import { ResourceDataContextProvider } from '@fabric-app/business/resource-detail/components/context/ResourceDataContextProvider';
import { DashboardHeader } from '@fabric-app/business/dashboard/components/DashboardHeader';
import { BadgePermissionRole } from '@fabric-app/business/permission/components/BadgePermissionRole';
import { MemberBarSpaceOrFolderWithLiveBlocks } from '@fabric-app/business/multiplayer/components/MemberBar/MemberBarSpaceOrFolderWithLiveBlocks';
import { IconFolderCreate, IconLinkWindow, IconPlusSmall } from '@fabric-app/icons/svg';
import { DropdownItemResourceUserPinHome } from '@fabric-app/business/resource-detail/components/DropdownItems/DropdownItemResourceUserPinHome';
import { DropdownItemResourceDeleteOrLeave } from '@fabric-app/business/resource-detail/components/DropdownItems/DropdownItemResourceDeleteOrLeave';
import { appRoutes } from '@fabric-app/business/app/routing/routes.config';
import { DashboardHeaderFilterSortResources } from '@fabric-app/business/dashboard/components/DashboardHeaderFilterSortResources';
import { ResourceTopBarPath } from '@fabric-app/business/resources/components/ResourceTopBarPath';
import { PermissionRole } from '@fabric/woody-client';
import { NoSsr } from '@fabric-app/ui/components/NoSSR';
import { AddToLibraryButton } from '@fabric-app/business/spaces/components/AddToLibraryButton';
import { useModalNewResourceContext } from '@fabric-app/business/resources/components/NewResource/ModalNewResourceContext';
import { NewResourceType } from '@fabric-app/business/resources/components/NewResource/config';
import { ViewModeSwitcher } from '@fabric-app/business/resources/components/ResourceList/ViewModeSwitcher/ViewModeSwitcher';
import { useResourceListPreferencesContext } from '@fabric-app/business/resources/components/ResourceList/contexts/resourceListContextPreferences';
import { HomeButton } from '@/src/modules/publishing/components/HomeButton';

type IntegrationViewProps = {
  overrideOnClose?: VoidFunction | null;
  folder: ResourceDetailFolder;
  readOnly: boolean;
  resources: OptimisticDraft<ResourceDetail>[];
  backRoute: Url;
  onClickFolder?: (resourceFolder: string) => void;
};

export const FolderHeader: React.FC<IntegrationViewProps> = (props) => {
  const { element: filterToggleButtonEl, ...filterVisibilityState } = useFilterToggleButton();
  const { viewMode } = useResourceListPreferencesContext();
  const shareToken = useContextShareToken();
  const accessToken = shareToken?.accessToken;
  const { isMobileView } = useResponsive();
  const router = useRouter();
  const { setNewModalOpen } = useModalNewResourceContext();

  const { overrideOnClose, folder, backRoute, readOnly, resources, onClickFolder } = props;

  const { openInTabs, fabricExtensionInstalled } = useOpenInTabs();
  const { disabledActions, userRole } = useResourcePermissionsByCurrentUser(folder);
  const { data: totals, refetch } = useQueryResourceSummary(folder.id, {
    refetchInterval: false,
  });

  useSocketIOParentRoomEventListener('RESOURCES:RELOAD', () => {
    refetch();
  });

  const { zoomLevel, setZoomLevel } = useUIStore(
    (s) => pick(s, ['zoomLevel', 'setZoomLevel']),
    shallow,
  );

  const handleCreateSubFolder = () => {
    setNewModalOpen(true, {
      type: NewResourceType.Folder,
      parent: {
        id: folder.id,
        name: folder.name,
      },
    });
  };

  const handleCreateNewResource = () => {
    setNewModalOpen(true);
  };

  const onOpenShareModal = disabledActions.manageSomePermissionRoles
    ? undefined
    : () => resourceSharingModal.open({ resourceId: folder.id });

  const dropdownMenu = (
    <DropdownMenu.Base
      renderTriggerElement={({ onOpenChange }) => (
        <DropdownMenu.TriggerButtonThreeDots onOpenChange={onOpenChange} />
      )}
    >
      <>
        {!accessToken && (
          <DropdownMenu.Group>
            <DropdownMenu.Item
              onSelect={handleCreateNewResource}
              iconElement={<IconPlusSmall />}
              data-testid="space-add-items-link"
              disabledReason={disabledActions.createChildResources}
            >
              Add items ...
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onClick={handleCreateSubFolder}
              iconElement={<IconFolderCreate />}
              data-testid="fdoc-list-context-menu-create-subfolder"
              disabledReason={disabledActions.createChildResources}
            >
              Create subfolder
            </DropdownMenu.Item>
            {fabricExtensionInstalled && (
              <DropdownMenu.Item
                onClick={() => openInTabs(resources, { newWindow: true })}
                data-testid="space-delete-button"
                iconElement={<IconLinkWindow />}
              >
                Open all in new window
              </DropdownMenu.Item>
            )}
          </DropdownMenu.Group>
        )}
        <DropdownMenu.Group>
          <DropdownItemExport
            label="Export folder content"
            resourceId={folder.id}
            toastProps={{ content: "Exporting this folder's content..." }}
          />
        </DropdownMenu.Group>
        {!accessToken && (
          <>
            <DropdownMenu.Group>
              <DropdownItemResourceUserPinHome />
            </DropdownMenu.Group>
            <DropdownMenu.Group>
              <DropdownItemResourceDeleteOrLeave
                onConfirmDelete={() => {
                  return router.push(backRoute);
                }}
                onConfirmLeave={() => {
                  return router.push(appRoutes.home);
                }}
              />
            </DropdownMenu.Group>
          </>
        )}
      </>
      {!isMobileView && ['Grid', 'Masonry'].includes(viewMode) && (
        <>
          <DropdownMenu.Separator />
          <DropdownMenu.Group>
            <ResourceUiDropdownShowControls uiContext="global" />
          </DropdownMenu.Group>
        </>
      )}
      <ZoomSliderWithinDropdownV2 viewMode={viewMode} onChange={setZoomLevel} value={zoomLevel} />
    </DropdownMenu.Base>
  );

  return (
    <ResourceDataContextProvider resource={folder}>
      <DashboardHeader>
        <DashboardHeader.Body>
          <DashboardHeader.Left>
            {overrideOnClose === null || !isMobileView ? null : (
              <BackButton onClick={overrideOnClose} />
            )}

            {!isMobileView && (
              <NoSsr>
                <HomeButton />
                <AddToLibraryButton type="folder" resource={folder} />
              </NoSsr>
            )}
            <ResourceTopBarPath resource={folder} onClickFolder={onClickFolder} />

            {folder && userRole !== PermissionRole.OWNER && shareToken?.type !== 'publishing' && (
              <BadgePermissionRole role={userRole} itemType="folder" />
            )}
          </DashboardHeader.Left>
          {!isMobileView && (
            <DashboardHeader.Right>
              {!isMobileView && filterToggleButtonEl}
              <ViewModeSwitcher />

              {!isMobileView && !readOnly && folder && (
                <MemberBarSpaceOrFolderWithLiveBlocks
                  resourceId={folder.id}
                  onOpenShareModal={onOpenShareModal}
                  type="folder"
                  renderShareButtonOutside
                  isPublicShared={folder.isShared || false}
                />
              )}
              {dropdownMenu}
            </DashboardHeader.Right>
          )}
        </DashboardHeader.Body>

        {isMobileView && (
          <DashboardHeader.Body>
            <DashboardHeader.Left>
              {!disabledActions.modifyChildResources && <ToggleResourceMobileMultiselectButton />}
              {filterToggleButtonEl}
              {folder && (
                <MemberBarSpaceOrFolderWithLiveBlocks
                  resourceId={folder.id}
                  onOpenShareModal={onOpenShareModal}
                  type="folder"
                  renderShareButtonOutside
                  isPublicShared={folder.isShared || false}
                />
              )}
            </DashboardHeader.Left>
            <DashboardHeader.Right>
              <NoSsr>
                <AddToLibraryButton type="folder" resource={folder} />
              </NoSsr>
              {dropdownMenu}
            </DashboardHeader.Right>
          </DashboardHeader.Body>
        )}
      </DashboardHeader>
      <DashboardHeaderFilterSortResources
        resourceTotals={totals}
        visible={filterVisibilityState.value}
        resourceId={folder?.id}
      />
    </ResourceDataContextProvider>
  );
};
